import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, ShieldCheck, Search, Megaphone, DollarSign, Building2, Facebook, Pause, Play, TrendingUp, AlertTriangle } from "lucide-react";
import { sr, actAs } from "../../lib/sr";

const money = (n) => "$" + Number(n || 0).toLocaleString();

const STATUS_TONE = {
  LIVE: "sr-badge-green", ACTIVE: "sr-badge-green", PUBLISHED: "sr-badge-green",
  PAUSED: "sr-badge-amber", DELETED: "sr-badge-gray",
};
const isPaused = (s) => s === "PAUSED";

export default function LiveCampaigns() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [src, setSrc] = useState("all");
  const [busyId, setBusyId] = useState(null);
  const [guard, setGuard] = useState(null); // {enabled, daily_cap}
  const [capInput, setCapInput] = useState("");
  const [testMode, setTestMode] = useState(null); // {enabled}

  const load = () => sr.adminLiveCampaigns().then(setData).catch(() => setData({ error: true }));
  useEffect(() => { load(); }, []);
  useEffect(() => { sr.adminSpendGuardGet().then((g) => { setGuard(g); setCapInput(g.daily_cap ? String(g.daily_cap) : ""); }).catch(() => {}); }, []);
  useEffect(() => { sr.adminTestModeGet().then(setTestMode).catch(() => {}); }, []);

  const saveTestMode = async (enabled) => {
    try {
      const t = await sr.adminTestModeSet(enabled);
      setTestMode(t);
      toast.success(enabled ? "Test Mode ON — nothing is released to the ad platform" : "Production Mode ON — campaigns can be released to the ad platform");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't change the mode"); }
  };

  const saveGuard = async (enabled) => {
    const cap = parseFloat(capInput || "0");
    if (enabled && !(cap > 0)) { toast.error("Enter a daily spend cap"); return; }
    try { const g = await sr.adminSpendGuardSet({ enabled, daily_cap: cap }); setGuard(g); toast.success(enabled ? `Auto-pause on at $${g.daily_cap}/day` : "Auto-pause safety off"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save safety rule"); }
  };

  if (!data) return <div className="sr-empty"><Loader2 className="sr-spin" /></div>;
  if (data.error) return <div className="sr-card"><p className="sr-muted">Could not load live campaigns. Admin access is required.</p></div>;

  const open = (c) => {
    // Jump into the owning client's workspace, then to the campaign/meta view.
    if (c.tenant_id) { actAs.set(c.tenant_id, c.tenant_name); sr.adminActAs(c.tenant_id).catch(() => {}); }
    window.location.assign(c.link || "/app");
  };

  const toggle = async (e, c) => {
    e.stopPropagation();
    const action = isPaused(c.status) ? "resume" : "pause";
    setBusyId(`${c.source}-${c.id}`);
    try {
      const r = await sr.toggleLiveCampaign({ source: c.source, id: c.id, action });
      toast.success(action === "pause" ? "Campaign paused" : "Campaign resumed");
      setData((d) => ({ ...d, campaigns: d.campaigns.map((x) => (x.source === c.source && x.id === c.id ? { ...x, status: r.status } : x)) }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't update the campaign");
    } finally { setBusyId(null); }
  };

  const s = q.trim().toLowerCase();
  const rows = (data.campaigns || []).filter((c) => {
    if (src !== "all" && c.source !== src) return false;
    if (!s) return true;
    return (c.tenant_name || "").toLowerCase().includes(s) || (c.name || "").toLowerCase().includes(s) || (c.channel || "").toLowerCase().includes(s);
  });

  const SRC = [["all", "All", data.total], ["internal", "Internal", data.internal_count], ["meta", "Meta Ads", data.meta_count]];

  return (
    <div data-testid="live-campaigns-page">
      <div style={{ marginBottom: 16 }}>
        <span className="sr-badge" style={{ marginBottom: 8 }}><ShieldCheck size={12} /> Admin · Live Campaigns</span>
        <h1 style={{ fontSize: 30 }}>Every campaign, across all clients</h1>
        <p className="sr-muted" style={{ marginTop: 6 }}>A portfolio-wide view of what's running right now — internal campaigns and Meta Ads, with live spend and results. Pause or resume any campaign here, or click a row to jump into that client's workspace.</p>
      </div>

      <div className="sr-grid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 12, marginBottom: 18 }} data-testid="live-campaigns-totals">
        <div className="sr-stat"><div className="sr-stat-label">Campaigns</div><div className="sr-stat-val">{data.total}</div></div>
        <div className="sr-stat"><div className="sr-stat-label">Spend (30d)</div><div className="sr-stat-val" data-testid="live-total-spend">{money(data.total_spend)}</div></div>
        <div className="sr-stat"><div className="sr-stat-label">Results (30d)</div><div className="sr-stat-val" data-testid="live-total-results">{Number(data.total_results || 0).toLocaleString()}</div></div>
        <div className="sr-stat"><div className="sr-stat-label">Alerts</div><div className="sr-stat-val" data-testid="live-total-alerts" style={{ color: data.alerts_count ? "#B42318" : undefined }}>{data.alerts_count || 0}</div></div>
        <div className="sr-stat"><div className="sr-stat-label">Meta / Internal</div><div className="sr-stat-val">{data.meta_count} / {data.internal_count}</div></div>
        <div className="sr-stat"><div className="sr-stat-label">Monthly budget</div><div className="sr-stat-val">{money(data.total_monthly_budget)}</div></div>
      </div>

      {/* Test Mode safety net — blocks any the ad platform release while ON */}
      {testMode && (
        <div className="sr-card" style={{ marginBottom: 14, display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", borderLeft: `3px solid ${testMode.enabled ? "#B8791B" : "#067647"}` }} data-testid="test-mode-bar">
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <ShieldCheck size={16} style={{ color: testMode.enabled ? "#B8791B" : "#067647" }} />
            <div>
              <div style={{ fontWeight: 700, color: "var(--navy)", fontSize: 14 }}>
                {testMode.enabled ? "Test Mode" : "Production Mode"}
                <span className={`sr-badge ${testMode.enabled ? "sr-badge-amber" : "sr-badge-green"}`} style={{ marginLeft: 8 }} data-testid="test-mode-status">{testMode.enabled ? "ON · Ad platform release blocked" : "LIVE · Ad platform release enabled"}</span>
              </div>
              <div className="sr-muted" style={{ fontSize: 12 }}>While Test Mode is ON, plans still flow through Approve → Activate → Ops confirm, but nothing is ever released to the ad platform — so no real money is spent.</div>
            </div>
          </div>
          <div style={{ marginLeft: "auto" }}>
            {testMode.enabled ? (
              <button className="sr-btn sr-btn-sm" onClick={() => { if (window.confirm("Switch to Production Mode? Campaigns will be able to release to the ad platform and spend real money.")) saveTestMode(false); }} data-testid="test-mode-go-production">Switch to Production Mode</button>
            ) : (
              <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => saveTestMode(true)} data-testid="test-mode-go-test">Back to Test Mode</button>
            )}
          </div>
        </div>
      )}

      {/* Auto-pause safety rule */}
      {guard && (
        <div className="sr-card" style={{ marginBottom: 14, display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", borderLeft: "3px solid #B42318" }} data-testid="spend-guard-bar">
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <ShieldCheck size={16} style={{ color: "#B42318" }} />
            <div>
              <div style={{ fontWeight: 700, color: "var(--navy)", fontSize: 14 }}>Auto-pause on spend spike</div>
              <div className="sr-muted" style={{ fontSize: 12 }}>Global fallback cap — auto-pauses a campaign whose daily spend goes over. Set a per-client cap on a client's page to override this.</div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginLeft: "auto", flexWrap: "wrap" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <span className="sr-muted" style={{ fontSize: 13 }}>$</span>
              <input className="sr-input" type="number" min="0" placeholder="Daily cap" value={capInput}
                onChange={(e) => setCapInput(e.target.value)} data-testid="spend-guard-cap" style={{ maxWidth: 120 }} />
              <span className="sr-muted" style={{ fontSize: 13 }}>/day</span>
            </div>
            {guard.enabled ? (
              <>
                <span className="sr-badge sr-badge-green" data-testid="spend-guard-on">On · ${guard.daily_cap}/day</span>
                <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => saveGuard(false)} data-testid="spend-guard-disable">Turn off</button>
                <button className="sr-btn sr-btn-sm" onClick={() => saveGuard(true)} data-testid="spend-guard-update">Update</button>
              </>
            ) : (
              <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={() => saveGuard(true)} data-testid="spend-guard-enable">Enable safety</button>
            )}
          </div>
        </div>
      )}

      <div className="sr-card" style={{ marginBottom: 14, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <Search size={16} style={{ color: "var(--muted)" }} />
        <input className="sr-input" placeholder="Search by client, campaign or channel" value={q} onChange={(e) => setQ(e.target.value)}
          data-testid="live-campaigns-search" style={{ border: "none", flex: 1, minWidth: 200 }} />
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }} data-testid="live-campaigns-source-filter">
          {SRC.map(([key, label, count]) => {
            const active = src === key;
            return (
              <button key={key} onClick={() => setSrc(key)} data-testid={`live-src-${key}`}
                style={{ border: "1px solid " + (active ? "var(--blue)" : "var(--border)"), borderRadius: 999,
                  background: active ? "var(--blue)" : "#fff", color: active ? "#fff" : "var(--navy)",
                  fontSize: 12, fontWeight: 700, padding: "6px 12px", cursor: "pointer" }}>
                {label} <span style={{ opacity: 0.75 }}>· {count}</span>
              </button>
            );
          })}
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="sr-card" data-testid="live-campaigns-empty"><p className="sr-muted">No campaigns match your search.</p></div>
      ) : (
        <div style={{ display: "grid", gap: 10 }}>
          {rows.map((c) => {
            const m = c.metrics || {};
            const rowBusy = busyId === `${c.source}-${c.id}`;
            return (
              <div key={`${c.source}-${c.id}`} className="sr-card sr-card-hover" data-testid={`live-campaign-${c.id}`}
                onClick={() => open(c)} style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
                {c.source === "meta" ? <Facebook size={18} style={{ color: "#1877F2", flexShrink: 0 }} /> : <Megaphone size={18} style={{ color: "var(--blue)", flexShrink: 0 }} />}
                <div style={{ flex: "1 1 auto", minWidth: 0 }}>
                  <div style={{ color: "var(--navy)", fontWeight: 700, fontSize: 15 }}>{c.name}</div>
                  <div className="sr-muted" style={{ fontSize: 12, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginTop: 2 }}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><Building2 size={12} /> {c.tenant_name}</span>
                    <span>· {c.channel}</span>
                    {c.objective ? <span>· {String(c.objective).replace("OUTCOME_", "").toLowerCase()}</span> : null}
                  </div>
                  {c.alert && (
                    <div data-testid={`live-alert-${c.id}`} style={{ marginTop: 6, display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 600,
                      color: c.alert.severity === "high" ? "#B42318" : "#B8791B",
                      background: c.alert.severity === "high" ? "#FEF3F2" : "#FFF8EB", border: "1px solid " + (c.alert.severity === "high" ? "#FECDCA" : "#FDE7B8"),
                      borderRadius: 6, padding: "3px 8px" }}>
                      <AlertTriangle size={12} /> {c.alert.message}
                    </div>
                  )}
                </div>
                <div style={{ textAlign: "right", minWidth: 84 }} data-testid={`live-metrics-${c.id}`}>
                  <div style={{ fontSize: 13, color: "var(--navy)", fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 4 }}>
                    <DollarSign size={13} style={{ color: "var(--blue)" }} />{money(m.spend)}
                  </div>
                  <div className="sr-muted" style={{ fontSize: 11, display: "flex", alignItems: "center", gap: 3, justifyContent: "flex-end" }}>
                    <TrendingUp size={11} /> {Number(m.results || 0).toLocaleString()} {m.results_label || "results"}
                  </div>
                </div>
                <span className={`sr-badge ${STATUS_TONE[c.status] || "sr-badge-gray"}`}>{c.status}</span>
                <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={(e) => toggle(e, c)} disabled={rowBusy} data-testid={`live-toggle-${c.id}`} style={{ flexShrink: 0 }}>
                  {rowBusy ? <Loader2 size={14} className="sr-spin" /> : isPaused(c.status) ? <Play size={14} /> : <Pause size={14} />}
                  {isPaused(c.status) ? "Resume" : "Pause"}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
